import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, userApi } from '../api';
import { clearAuthToken } from '../auth';
import UserTable from '../components/UserTable';
import UserModal from '../components/UserModal';

function Dashboard({ onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const navigate = useNavigate();

  // Fetch lightweight user list
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await userApi.getList();
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch full user details
  const fetchUserDetails = async (userId) => {
    setLoadingDetails(true);
    try {
      const response = await userApi.getById(userId);
      setSelectedUser(response.data);
      setShowModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleLogout = async () => {
    try {
      await authApi.logout();
      clearAuthToken();
      onLogout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      clearAuthToken();
      onLogout();
      navigate('/login');
    }
  };

  const handleAddNew = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEdit = (userId) => {
    fetchUserDetails(userId);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async () => {
    await fetchUsers();
    handleCloseModal();
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userApi.delete(id);
        setUsers(users.filter(u => u.id !== id));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Error message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Add New User button */}
        <div className="mb-6">
          <button
            onClick={handleAddNew}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
          >
            + Add New User
          </button>
        </div>

        {/* Users table */}
        {loading ? (
          <div className="text-center py-8 text-gray-600">Loading users...</div>
        ) : (
          <UserTable
            users={users}
            onEdit={handleEdit}
            onDelete={handleDeleteUser}
          />
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <UserModal
          user={selectedUser}
          onClose={handleCloseModal}
          onSave={handleSaveUser}
        />
      )}
    </div>
  );
}

export default Dashboard;
